import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../environment";
import { handleAuthError, getAuthHeaders } from "../../utils/authUtils";
import { Link } from "react-router-dom";
import { initialSettings } from "../../core/common/selectoption/selectoption";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import DatePicker from "react-datepicker";
import DateRangePicker from "react-bootstrap-daterangepicker";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { DEFAULT_AVATAR } from "../../environment";
import { useTabVisibility } from "../../hooks/useTabVisibility";
import TaskDetailModal from "../TaskDetailModal";

interface TasksProps {
  tenant_id?: number | string | null;
  related_entity_id?: number | string | null;
  related_entity_name: string;
  refreshTrigger?: number;
  onActivityUpdate?: () => void;
}
const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
  if (e.currentTarget.src !== DEFAULT_AVATAR) {
    e.currentTarget.src = DEFAULT_AVATAR;
  }
};
const formatTaskDate = (dateString: string | null): string => {
  if (!dateString) return "Not set";

  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

interface TaskType {
  task_id: number;
  title: string;
  category: string;
  start_date: string | null;
  due_date: string | null;
  priority: string;
  status: string;
  description: string;
  create_meeting?: boolean;
  attendees?: string[];
  created_datetime: string | null;
  related_entity_name: string;
  related_entity_id: number;
  responsible_persons: {
    user_id: number;
    full_name: string;
    avatar_url: string;
  };
  responsible_team: {
    team_id: number;
    name: string;
  };
  created_by: {
    user_id: number;
    full_name: string;
  };
  tenant_id: number;
  tenant: {
    tenant_id: number;
    tenant_name: string;
  };
}

interface TaskApiResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: TaskType[];
}

interface User {
  user_id: number;
  tenant_user_id: number;
  user_first_name: string;
  user_last_name: string;
  email: string;
  avatar_url?: string;
  role_display_name: string;
  is_active: boolean;
  tenant_id: number;
}

interface Team {
  team_id: number;
  name: string;
}

interface TeamMember {
  team_user_id: number;
  user_full_name: string;
  team_id: number;
  user_id: number;
}

interface TaskFormData {
  title: string;
  responsible_person: number | null;
  assigned_team: number | null;
  start_date: Date | null;
  due_date: Date | null;
  category: string;
  priority: string;
  status: string;
  description: string;
  create_meeting: boolean;
  attendees: string[];
}

function groupTasksByDate(tasks: TaskType[]) {
  const groups: Record<string, TaskType[]> = {};
  tasks.forEach((task) => {
    if (!task.created_datetime) return;
    const date = new Date(task.created_datetime);

    const key = date.toISOString().split("T")[0];
    if (!groups[key]) groups[key] = [];
    groups[key].push(task);
  });
  return groups;
}

function getDateLabel(dateStr: string) {
  const today = new Date();
  const date = new Date(dateStr);
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  const isToday =
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate();
  const isYesterday =
    date.getFullYear() === yesterday.getFullYear() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getDate() === yesterday.getDate();
  if (isToday) return "Today";
  if (isYesterday) return "Yesterday";
  return date.toLocaleDateString("en-US", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

const getStatusBadgeColor = (status: string): string => {
  switch (status.toLowerCase()) {
    case "pending":
      return "bg-info";
    case "in progress":
      return "bg-warning";
    case "completed":
      return "bg-success";
    default:
      return "bg-secondary";
  }
};

const getPriorityBadgeColor = (priority: string): string => {
  switch (priority.toLowerCase()) {
    case "low":
      return "bg-success";
    case "medium":
      return "bg-warning";
    case "high":
      return "bg-danger";
    default:
      return "bg-info";
  }
};

const Task: React.FC<TasksProps> = ({
  tenant_id,
  related_entity_id,
  related_entity_name,
  refreshTrigger,
  onActivityUpdate,
}) => {
  const handleStartDateChange = (date: Date | null) => {
    handleInputChange("start_date", date);
    const newDueDate = formData.due_date;
    const dateError = validateDates(date, newDueDate);
    setDateValidationError(dateError);
  };
  const handleDueDateChange = (date: Date | null) => {
    handleInputChange("due_date", date);
    const newStartDate = formData.start_date;
    const dateError = validateDates(newStartDate, date);
    setDateValidationError(dateError);
  };

  const [tasks, setTasks] = useState<TaskType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [displayCount, setDisplayCount] = useState<number>(20);
  const [nextUrl, setNextUrl] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);

  const [activeFilter, setActiveFilter] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [dateRange, setDateRange] = useState<{ start?: string; end?: string }>(
    {}
  );

  const [users, setUsers] = useState<User[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [formData, setFormData] = useState<TaskFormData>({
    title: "",
    responsible_person: null,
    assigned_team: null,
    start_date: null,
    due_date: null,
    category: "",
    priority: "",
    status: "",
    description: "",
    create_meeting: false,
    attendees: [],
  });
  const [attendeeInput, setAttendeeInput] = useState("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [selectedTask, setSelectedTask] = useState<TaskType | null>(null);
  const [editFormData, setEditFormData] = useState<TaskFormData>({
    title: "",
    responsible_person: null,
    assigned_team: null,
    start_date: null,
    due_date: null,
    category: "",
    priority: "medium",
    status: "pending",
    description: "",
    create_meeting: false,
    attendees: [],
  });
  const [editAttendeeInput, setEditAttendeeInput] = useState("");
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  const [taskToDelete, setTaskToDelete] = useState<TaskType | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const [dateValidationError, setDateValidationError] = useState<string>("");
  const [editDateValidationError, setEditDateValidationError] =
    useState<string>("");
  const [dataLoaded, setDataLoaded] = useState(false);

  const [isTaskModalVisible, setIsTaskModalVisible] = useState(false);
  const [isLoadingTaskDetails, setIsLoadingTaskDetails] = useState(false);
  const [taskDetails, setTaskDetails] = useState<any>(null);

  const statusOptions = [
    { value: "pending", label: "Pending" },
    { value: "in progress", label: "In Progress" },
    { value: "completed", label: "Completed" },
  ];

  const priorityOptions = [
    { value: "low", label: "Low" },
    { value: "medium", label: "Medium" },
    { value: "high", label: "High" },
  ];

  const categoryOptions = [
    { value: "Calls", label: "Calls" },
    { value: "Email", label: "Email" },
    { value: "Meeting", label: "Meeting" },
    { value: "Follow-up", label: "Follow-up" },
    { value: "Research", label: "Research" },
    { value: "Administrative", label: "Administrative" },
  ];

  const MySwal = withReactContent(Swal);

  const { hasBeenActivated } = useTabVisibility({
    tabId: "tasks",
    onTabActivated: () => {
      if (
        !dataLoaded &&
        tenant_id &&
        related_entity_id &&
        related_entity_name
      ) {
        fetchTasks("all", "", undefined, undefined);
        fetchUsers();
        fetchTeams();
        setDataLoaded(true);
      }
    },
  });

  const validateDates = (
    startDate: Date | null,
    dueDate: Date | null
  ): string => {
    if (startDate && dueDate && startDate > dueDate) {
      return "Due Date Time cannot be before Start Date Time";
    }
    return "";
  };

  const isFormValid = () => {
    return (
      formData.title.trim() !== "" &&
      formData.start_date !== null &&
      formData.due_date !== null &&
      formData.category.trim() !== "" &&
      formData.priority.trim() !== "" &&
      formData.status.trim() !== "" &&
      formData.description.trim() !== ""
    );
  };

  const isEditFormValid = () => {
    return (
      editFormData.title.trim() !== "" &&
      editFormData.start_date !== null &&
      editFormData.due_date !== null &&
      editFormData.category.trim() !== "" &&
      editFormData.priority.trim() !== "" &&
      editFormData.status.trim() !== "" &&
      editFormData.description.trim() !== ""
    );
  };

  const fetchTasks = useCallback(
    async (
      filter: string = "all",
      search: string = "",
      dateStart?: string,
      dateEnd?: string,
      url?: string
    ) => {
      if (!tenant_id || !related_entity_id || !related_entity_name) return;
      setLoading((prev) => (url ? prev : true));
      setError(null);
      try {
        let endpoint =
          url ||
          `${API_BASE_URL}/task/?tenant_id=${tenant_id}&related_entity_id=${related_entity_id}&related_entity_name=${related_entity_name}&page_size=50`;

        if (!url) {
          if (filter === "completed") {
            endpoint += "&status=completed";
          } else if (filter === "important") {
            endpoint += "&priority=high";
          }

          if (search.trim()) {
            endpoint += `&title=${encodeURIComponent(search.trim())}`;
          }

          if (dateStart) {
            endpoint += `&created_datetime_gte=${encodeURIComponent(
              dateStart
            )}`;
          }
          if (dateEnd) {
            endpoint += `&created_datetime_lte=${encodeURIComponent(dateEnd)}`;
          }
        }

        const response = await axios.get<TaskApiResponse>(endpoint, {
          headers: getAuthHeaders(),
        });

        setTasks((prev) =>
          url ? [...prev, ...response.data.results] : response.data.results
        );
        setNextUrl(response.data.next);

        if (!url) {
          setDisplayCount(20);
        }
      } catch (err: any) {
        handleAuthError(err);
        setError("Error loading tasks.");
      } finally {
        setLoading(false);
      }
    },
    [tenant_id, related_entity_id, related_entity_name]
  );

  const handleFilterChange = (filter: string) => {
    setActiveFilter(filter);
    setDisplayCount(20);
    setNextUrl(null);
    fetchTasks(filter, searchTerm, dateRange.start, dateRange.end);
  };

  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDisplayCount(20);
      setNextUrl(null);
      fetchTasks(activeFilter, value, dateRange.start, dateRange.end);
    }, 500);
  };

  const handleTaskClick = async (task: TaskType) => {
    if (!task || !task.task_id || !task.title || task.title.trim() === "") {
      return;
    }

    setIsTaskModalVisible(true);
    setIsLoadingTaskDetails(true);

    try {
      const response = await axios.get(
        `${API_BASE_URL}/task/${task.task_id}/`,
        {
          headers: getAuthHeaders(),
        }
      );
      setTaskDetails(response.data);
    } catch (err: any) {
      console.error("Error loading task details:", err);
      handleAuthError(err);
    } finally {
      setIsLoadingTaskDetails(false);
    }
  };

  const handleTaskModalClose = () => {
    setIsTaskModalVisible(false);
    setTaskDetails(null);
    setIsLoadingTaskDetails(false);
  };

  const handleTaskUpdate = () => {
    fetchTasks(activeFilter, searchTerm, dateRange.start, dateRange.end);
    if (onActivityUpdate) {
      onActivityUpdate();
    }
  };

  const handleDateRangeChange = (start?: string, end?: string) => {
    setDisplayCount(20);
    setNextUrl(null);

    if (!start || !end) {
      setDateRange({});
      fetchTasks(activeFilter, searchTerm, undefined, undefined);
    } else {
      setDateRange({ start, end });
      fetchTasks(activeFilter, searchTerm, start, end);
    }
  };

  const hasActiveFilters = () => {
    return (
      activeFilter !== "all" ||
      searchTerm.trim() !== "" ||
      dateRange.start !== undefined ||
      dateRange.end !== undefined
    );
  };

  const clearAllFilters = () => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    setActiveFilter("all");
    setSearchTerm("");
    setDateRange({});
    setDisplayCount(20);
    setNextUrl(null);
    fetchTasks("all", "", undefined, undefined);
  };

  const handleLoadMore = async () => {
    if (displayCount < validTasks.length) {
      setDisplayCount((prev) => Math.min(prev + 20, validTasks.length));
      return;
    }

    if (nextUrl) {
      setLoadingMore(true);
      try {
        const prevLength = tasks.length;
        await fetchTasks(
          activeFilter,
          searchTerm,
          dateRange.start,
          dateRange.end,
          nextUrl
        );

        setTimeout(() => {
          setDisplayCount((prev) => {
            const newValidTasks = tasks.filter(
              (task) =>
                task && task.task_id && task.title && task.title.trim() !== ""
            );
            const newLength = newValidTasks.length;
            if (newLength > prevLength) {
              return Math.min(prev + 20, newLength);
            }
            return newLength;
          });
        }, 100);
      } catch (error) {
        console.error("Error loading more tasks:", error);
      } finally {
        setLoadingMore(false);
      }
    }
  };

  const getTodaysTasks = () => {
    const today = new Date();
    const todayString = today.toISOString().split("T")[0];
    const visibleTasks = tasks.slice(0, displayCount);

    return visibleTasks.filter((task) => {
      if (!task.created_datetime) return false;
      const taskDate = new Date(task.created_datetime);
      const taskDateString = taskDate.toISOString().split("T")[0];
      return taskDateString === todayString;
    });
  };

  const getRecentTasks = () => {
    return getTodaysTasks().filter(
      (task) => task && task.task_id && task.title && task.title.trim() !== ""
    );
  };

  const fetchUsers = useCallback(async () => {
    if (!tenant_id) return;
    try {
      const endpoint = `${API_BASE_URL}/tenant-user/?tenant_id=${tenant_id}`;
      const response = await axios.get(endpoint, {
        headers: getAuthHeaders(),
      });
      const userData = response.data.results || response.data;
      setUsers(userData);
    } catch (err: any) {
      handleAuthError(err);
      console.error("Error loading users:", err);
    }
  }, [tenant_id]);

  const fetchTeams = useCallback(async () => {
    if (!tenant_id) return;
    try {
      const endpoint = `${API_BASE_URL}/team/?tenant_id=${tenant_id}`;
      const response = await axios.get(endpoint, {
        headers: getAuthHeaders(),
      });
      setTeams(response.data.results || response.data);
    } catch (err: any) {
      handleAuthError(err);
      console.error("Error loading teams:", err);
    }
  }, [tenant_id]);

  const fetchTeamMembers = useCallback(async (teamId: number) => {
    if (!teamId) {
      setTeamMembers([]);
      return;
    }
    try {
      const endpoint = `${API_BASE_URL}/team-user/?team_id=${teamId}`;
      const response = await axios.get(endpoint, {
        headers: getAuthHeaders(),
      });
      setTeamMembers(response.data.results || response.data);
    } catch (err: any) {
      handleAuthError(err);
      console.error("Error loading team members:", err);
      setTeamMembers([]);
    }
  }, []);

  useEffect(() => {
    if (
      hasBeenActivated &&
      !dataLoaded &&
      tenant_id &&
      related_entity_id &&
      related_entity_name
    ) {
      fetchTasks("all", "", undefined, undefined);
      fetchUsers();
      fetchTeams();
      setDataLoaded(true);
    }
  }, [
    hasBeenActivated,
    dataLoaded,
    tenant_id,
    related_entity_id,
    related_entity_name,
  ]);

  useEffect(() => {
    if (dataLoaded && tenant_id && related_entity_id && related_entity_name) {
      fetchTasks(activeFilter, searchTerm, dateRange.start, dateRange.end);
    }
  }, [
    activeFilter,
    searchTerm,
    dateRange.start,
    dateRange.end,
    refreshTrigger,
    dataLoaded,
    tenant_id,
    related_entity_id,
    related_entity_name,
  ]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleInputChange = (field: keyof TaskFormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (field === "assigned_team") {
      if (value) {
        fetchTeamMembers(value);
      } else {
        setTeamMembers([]);
      }

      setFormData((prev) => ({
        ...prev,
        responsible_person: null,
      }));
    }
  };

  const handleEditInputChange = (field: keyof TaskFormData, value: any) => {
    setEditFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (field === "assigned_team") {
      if (value) {
        fetchTeamMembers(value);
      } else {
        setTeamMembers([]);
      }

      setEditFormData((prev) => ({
        ...prev,
        responsible_person: null,
      }));
    }
  };

  const handleEditStartDateChange = (date: Date | null) => {
    handleEditInputChange("start_date", date);
    const newDueDate = editFormData.due_date;
    const dateError = validateDates(date, newDueDate);
    setEditDateValidationError(dateError);
  };
  const handleEditDueDateChange = (date: Date | null) => {
    handleEditInputChange("due_date", date);
    const newStartDate = editFormData.start_date;
    const dateError = validateDates(newStartDate, date);
    setEditDateValidationError(dateError);
  };

  const handleEditTask = (task: TaskType) => {
    setSelectedTask(task);
    setEditFormData({
      title: task.title,
      responsible_person: task.responsible_persons?.user_id || null,
      assigned_team: task.responsible_team?.team_id || null,
      start_date: task.start_date ? new Date(task.start_date) : null,
      due_date: task.due_date ? new Date(task.due_date) : null,
      category: task.category,
      priority: task.priority,
      status: task.status,
      description: task.description,
      create_meeting: task.create_meeting || false,
      attendees: task.attendees || [],
    });

    if (task.responsible_team?.team_id) {
      fetchTeamMembers(task.responsible_team.team_id);
    } else {
      setTeamMembers([]);
    }

    setEditDateValidationError("");
  };

  const handleDeleteTask = (task: TaskType) => {
    setTaskToDelete(task);
  };

  const confirmDeleteTask = async () => {
    if (!taskToDelete) return;

    setIsDeleting(true);
    try {
      await axios.delete(`${API_BASE_URL}/task/${taskToDelete.task_id}/`, {
        headers: getAuthHeaders(),
      });

      setTaskToDelete(null);

      fetchTasks(activeFilter, searchTerm, dateRange.start, dateRange.end);

      MySwal.fire({
        title: "Success!",
        text: "Task deleted successfully!",
        icon: "success",
        confirmButtonText: "OK",
        confirmButtonColor: "#3085d6",
      });

      if (onActivityUpdate) {
        onActivityUpdate();
      }
    } catch (err: any) {
      handleAuthError(err);

      MySwal.fire({
        title: "Error!",
        text: "Failed to delete task. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#d33",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tenant_id || !related_entity_id || !related_entity_name) return;

    const dateError = validateDates(formData.start_date, formData.due_date);
    if (dateError) {
      return;
    }

    setIsSubmitting(true);
    try {
      const currentUser = JSON.parse(localStorage.getItem("user") || "{}");

      const payload = {
        title: formData.title,
        responsible_persons: formData.responsible_person,
        responsible_team: formData.assigned_team,
        start_date: formData.start_date
          ? formData.start_date.toISOString()
          : null,
        due_date: formData.due_date ? formData.due_date.toISOString() : null,
        category: formData.category,
        priority: formData.priority,
        status: formData.status,
        description: formData.description,
        tenant_id: tenant_id,
        created_by: currentUser.user_id || currentUser.id || currentUser.userId,
        related_entity_id: related_entity_id,
        related_entity_name: related_entity_name,
        create_meeting: formData.create_meeting,
        attendees: formData.attendees,
      };

      const response = await axios.post(`${API_BASE_URL}/task/`, payload, {
        headers: getAuthHeaders(),
      });

      setFormData({
        title: "",
        responsible_person: null,
        assigned_team: null,
        start_date: null,
        due_date: null,
        category: "",
        priority: "",
        status: "",
        description: "",
        create_meeting: false,
        attendees: [],
      });
      setAttendeeInput("");

      setTeamMembers([]);
      setDateValidationError("");

      fetchTasks(activeFilter, searchTerm, dateRange.start, dateRange.end);

      MySwal.fire({
        title: "Success!",
        text: "Task created successfully!",
        icon: "success",
        confirmButtonText: "OK",
        confirmButtonColor: "#3085d6",
      }).then(() => {
        closeOffcanvas("offcanvas_add");
      });

      if (onActivityUpdate) {
        onActivityUpdate();
      }
    } catch (err: any) {
      handleAuthError(err);

      MySwal.fire({
        title: "Error!",
        text: "Failed to create task. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#d33",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      !tenant_id ||
      !related_entity_id ||
      !related_entity_name ||
      !selectedTask
    )
      return;

    const dateError = validateDates(
      editFormData.start_date,
      editFormData.due_date
    );
    if (dateError) {
      return;
    }

    setIsUpdating(true);
    try {
      const currentUser = JSON.parse(localStorage.getItem("user") || "{}");

      const payload = {
        title: editFormData.title,
        responsible_persons: editFormData.responsible_person,
        responsible_team: editFormData.assigned_team,
        start_date: editFormData.start_date
          ? editFormData.start_date.toISOString()
          : null,
        due_date: editFormData.due_date
          ? editFormData.due_date.toISOString()
          : null,
        category: editFormData.category,
        priority: editFormData.priority,
        status: editFormData.status,
        description: editFormData.description,
        tenant_id: tenant_id,
        updated_by: currentUser.user_id || currentUser.id || currentUser.userId,
        related_entity_id: related_entity_id,
        related_entity_name: related_entity_name,
        create_meeting: editFormData.create_meeting,
        attendees: editFormData.attendees,
      };

      await axios.put(
        `${API_BASE_URL}/task/${selectedTask.task_id}/`,
        payload,
        {
          headers: getAuthHeaders(),
        }
      );

      setSelectedTask(null);
      setEditFormData({
        title: "",
        responsible_person: null,
        assigned_team: null,
        start_date: null,
        due_date: null,
        category: "",
        priority: "medium",
        status: "pending",
        description: "",
        create_meeting: false,
        attendees: [],
      });
      setEditAttendeeInput("");

      setTeamMembers([]);
      setEditDateValidationError("");

      fetchTasks(activeFilter, searchTerm, dateRange.start, dateRange.end);

      MySwal.fire({
        title: "Success!",
        text: "Task updated successfully!",
        icon: "success",
        confirmButtonText: "OK",
        confirmButtonColor: "#3085d6",
      }).then(() => {
        closeOffcanvas("offcanvas_edit");
      });

      if (onActivityUpdate) {
        onActivityUpdate();
      }
    } catch (err: any) {
      handleAuthError(err);

      MySwal.fire({
        title: "Error!",
        text: "Failed to update task. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#d33",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const closeOffcanvas = (offcanvasId: string) => {
    const modal = document.getElementById(offcanvasId);
    if (modal) {
      const closeButton = modal.querySelector(
        '[data-bs-dismiss="offcanvas"]'
      ) as HTMLElement;
      if (closeButton) {
        closeButton.click();
      } else {
        try {
          if (typeof window !== "undefined" && (window as any).bootstrap) {
            const bsOffcanvas = (window as any).bootstrap.Offcanvas.getInstance(
              modal
            );
            if (bsOffcanvas) {
              bsOffcanvas.hide();
            } else {
              const newOffcanvas = new (window as any).bootstrap.Offcanvas(
                modal
              );
              newOffcanvas.hide();
            }
          } else {
            throw new Error("Bootstrap not available");
          }
        } catch (error) {
          console.warn("Bootstrap Offcanvas not available, using manual close");

          modal.classList.remove("show");
          modal.setAttribute("aria-hidden", "true");
          document.body.classList.remove("offcanvas-open");
          document.body.style.overflow = "";
          document.body.style.paddingRight = "";

          const backdrops = document.querySelectorAll(".offcanvas-backdrop");
          backdrops.forEach((backdrop) => backdrop.remove());
        }
      }
    }

    if (offcanvasId === "offcanvas_add") {
      setTeamMembers([]);
      setFormData({
        title: "",
        responsible_person: null,
        assigned_team: null,
        start_date: null,
        due_date: null,
        category: "",
        priority: "",
        status: "",
        description: "",
        create_meeting: false,
        attendees: [],
      });
      setAttendeeInput("");
      setDateValidationError("");
    }
  };

  const userOptions = users.map((user) => ({
    value: user.user_id,
    label:
      `${user.user_first_name} ${user.user_last_name}`.trim() || "Unknown User",
  }));

  const teamMemberOptions = teamMembers.map((member: any) => ({
    value: member.user_id || member.user?.user_id || member.user?.id || member.id || member.team_user_id,
    label: member.user_full_name || member.user?.full_name || member.user?.first_name || "Unknown User",
  }));

  const teamOptions = teams.map((team) => ({
    value: team.team_id,
    label: team.name,
  }));

  const validTasks = tasks.filter(
    (task) => task && task.task_id && task.title && task.title.trim() !== ""
  );
  const visibleTasks = validTasks.slice(0, displayCount);
  const groupedTasks = groupTasksByDate(visibleTasks);

  const today = new Date();
  const todayString = today.toISOString().split("T")[0];
  const { [todayString]: todayTasksFromGrouped, ...tasksWithoutToday } =
    groupedTasks;

  const getRecentSectionData = () => {
    const recentTasks = getRecentTasks();
    const title = "Recent";
    return { title, tasks: recentTasks };
  };

  const dateKeys = Object.keys(tasksWithoutToday);

  const hasTasks = validTasks.length > 0;

  const getSafeCollapseId = (prefix: string, key: string) => {
    return `${prefix}-${key.replace(/[^a-zA-Z0-9]/g, "_")}`;
  };

  if (!tenant_id || !related_entity_id || !related_entity_name) {
    return (
      <div className="tab-pane fade" id="tasks">
        <div className="card">
          <div className="card-body">
            <div className="text-center py-4">
              <p className="text-muted">
                Unable to load tasks. Missing required information.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>
        {`
          .task-wrap {
            transition: background-color 0.2s ease;
          }
          .task-wrap:hover {
            background-color: #f8f9fa !important;
          }
          .task-wrap .task-actions {
            pointer-events: auto;
          }
          .task-wrap .dropdown {
            pointer-events: auto;
          }
        `}
      </style>
      {/* Tasks Tab Content */}
      <div className="tab-pane fade" id="tasks">
        <div className="card">
          <div className="card-header">
            {/* Search */}
            <div className="row align-items-center">
              <div className="col-sm-4">
                <div className="icon-form mb-sm-0">
                  <span className="form-icon">
                    <i className="ti ti-search" />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search Task"
                    value={searchTerm ?? ""}
                    onChange={handleSearchChange}
                  />
                </div>
              </div>
              <div className="col-sm-8">
                <div className="d-flex align-items-center flex-wrap row-gap-2 justify-content-sm-end mb-3 mb-sm-0">
                  {hasActiveFilters() && (
                    <button
                      type="button"
                      className="btn btn-outline-danger me-2"
                      onClick={clearAllFilters}
                    >
                      <i className="ti ti-filter-x me-1" />
                      Remove Filters
                    </button>
                  )}
                  <Link
                    to="#"
                    className="btn btn-primary"
                    data-bs-toggle="offcanvas"
                    data-bs-target="#offcanvas_add"
                  >
                    <i className="ti ti-square-rounded-plus me-2" />
                    Add New Tasks
                  </Link>
                </div>
              </div>
            </div>
            {/* /Search */}
          </div>

          <div className="card-body">
            <>
              {/* Filter */}
              <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-2 mb-4">
                <div className="sort-dropdown drop-down task-drops">
                  <h4>All Tasks</h4>
                </div>
                <div className="d-flex align-items-center flex-wrap row-gap-2">
                  <div className="icon-form me-2">
                    <span className="form-icon">
                      <i className="ti ti-calendar" />
                    </span>
                    <DateRangePicker
                      initialSettings={{
                        ...initialSettings,
                        startDate: dateRange.start
                          ? new Date(dateRange.start)
                          : new Date(),
                        endDate: dateRange.end
                          ? new Date(dateRange.end)
                          : (() => {
                            const tomorrow = new Date();
                            tomorrow.setDate(tomorrow.getDate() + 1);
                            return tomorrow;
                          })(),
                      }}
                      onApply={(event: any, picker: any) => {
                        const startDate = picker.startDate.format("YYYY-MM-DD");
                        const endDate = picker.endDate.format("YYYY-MM-DD");
                        handleDateRangeChange(startDate, endDate);
                      }}
                      onCancel={() => {
                        handleDateRangeChange();
                      }}
                    >
                      <input
                        className="form-control bookingrange"
                        type="text"
                        placeholder="Select date range"
                        value={
                          dateRange.start && dateRange.end
                            ? `${new Date(
                              dateRange.start
                            ).toLocaleDateString()} - ${new Date(
                              dateRange.end
                            ).toLocaleDateString()}`
                            : (() => {
                              const today = new Date();
                              const tomorrow = new Date();
                              tomorrow.setDate(today.getDate() + 1);
                              return `${today.toLocaleDateString()} - ${tomorrow.toLocaleDateString()} `;
                            })()
                        }
                        readOnly
                      />
                    </DateRangePicker>
                  </div>
                </div>
              </div>
              {/* /Filter */}
            </>

            {loading && (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            )}

            {!loading && getRecentSectionData().tasks.length > 0 && (
              <div className="task-wrapper">
                <div>
                  <h4>
                    {getRecentSectionData().title}&nbsp;
                    <span className="text-muted" style={{ fontSize: "12px" }}>
                      ({getRecentSectionData().tasks.length})
                    </span>
                  </h4>
                </div>
                <div className="tasks-activity tasks collapse show" id="recent">
                  <ul>
                    {getRecentSectionData().tasks.map((task) => {
                      const avatar =
                        task.responsible_persons?.avatar_url || DEFAULT_AVATAR;
                      return (
                        <li
                          className="task-wrap"
                          key={task.task_id}
                        >
                          <div
                            className="task-info"
                          >
                            <p
                              onClick={() => handleTaskClick(task)}
                              style={{ cursor: "pointer" }}
                            >
                              {task.title}
                            </p>
                            <span
                              className={`badge badge-tag ${getPriorityBadgeColor(
                                task.priority
                              )}`}
                            >
                              {task.priority}
                            </span>
                            <span
                              className={`badge badge-tag ${getStatusBadgeColor(
                                task.status
                              )}`}
                            >
                              {task.status}
                            </span>
                          </div>
                          <div className="task-actions">
                            <ul>
                              <li className="task-time">
                                <div className="d-flex flex-column gap-2 mb-2">
                                  <span className="badge badge-tag bg-light text-dark">
                                    <i className="ti ti-calendar-due me-1"></i>
                                    Due: {formatTaskDate(task.due_date)}
                                  </span>
                                </div>
                              </li>
                              <li className="task-owner">
                                <div className="task-user d-flex align-items-center">
                                  {task.responsible_persons?.full_name ? (
                                    <img
                                      src={avatar}
                                      alt="img"
                                      data-bs-toggle="tooltip"
                                      data-bs-placement="top"
                                      title={task.responsible_persons.full_name}
                                      style={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: "50%",
                                        objectFit: "cover",
                                        cursor: "pointer",
                                      }}
                                      onError={handleImageError}
                                    />
                                  ) : (
                                    <div
                                      className="d-flex align-items-center justify-content-center bg-light rounded-circle"
                                      data-bs-toggle="tooltip"
                                      data-bs-placement="top"
                                      title="No assigned user"
                                      style={{
                                        width: 32,
                                        height: 32,
                                        cursor: "pointer",
                                        opacity: 0.6,
                                      }}
                                    >
                                      <i
                                        className="ti ti-user-off text-muted"
                                        style={{ fontSize: "20px" }}
                                      ></i>
                                    </div>
                                  )}
                                </div>
                                <div className="dropdown table-action">
                                  <Link
                                    to="#"
                                    className="action-icon "
                                    data-bs-toggle="dropdown"
                                    aria-expanded="false"
                                  >
                                    <i className="fa fa-ellipsis-v" />
                                  </Link>
                                  <div className="dropdown-menu dropdown-menu-right">
                                    <Link
                                      className="dropdown-item"
                                      data-bs-toggle="offcanvas"
                                      to="#"
                                      data-bs-target="#offcanvas_edit"
                                      onClick={() => handleEditTask(task)}
                                    >
                                      <i className="ti ti-edit text-blue"></i>{" "}
                                      Edit
                                    </Link>
                                    <Link
                                      className="dropdown-item"
                                      to="#"
                                      data-bs-toggle="modal"
                                      data-bs-target="#delete_activity"
                                      onClick={() => handleDeleteTask(task)}
                                    >
                                      <i className="ti ti-trash text-danger" />{" "}
                                      Delete
                                    </Link>
                                  </div>
                                </div>
                              </li>
                            </ul>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            )}

            {!loading && !error && !hasTasks && (
              <div className="text-center py-4 text-muted">No tasks found.</div>
            )}

            {!loading &&
              !error &&
              hasTasks &&
              dateKeys.map((dateKey) => {
                const collapseId = getSafeCollapseId("date", dateKey);
                return (
                  <div className="task-wrapper" key={dateKey}>
                    <div>
                      <h4>
                        {getDateLabel(dateKey)}&nbsp;
                        <span
                          className="text-muted"
                          style={{ fontSize: "12px" }}
                        >
                          ({tasksWithoutToday[dateKey].length})
                        </span>
                      </h4>
                    </div>
                    <div
                      className="tasks-activity tasks collapse show"
                      id={collapseId}
                    >
                      <ul>
                        {tasksWithoutToday[dateKey].map((task) => {
                          const avatar =
                            task.responsible_persons?.avatar_url ||
                            DEFAULT_AVATAR;
                          return (
                            <li
                              className="task-wrap"
                              key={task.task_id}
                            >
                              <div
                                className="task-info"
                              >
                                <p
                                  onClick={() => handleTaskClick(task)}
                                  style={{ cursor: "pointer" }}
                                >
                                  {task.title}
                                </p>
                                <span
                                  className={`badge badge-tag ${getPriorityBadgeColor(
                                    task.priority
                                  )}`}
                                >
                                  {task.priority}
                                </span>
                                <span
                                  className={`badge badge-tag ${getStatusBadgeColor(
                                    task.status
                                  )}`}
                                >
                                  {task.status}
                                </span>
                              </div>
                              <div className="task-actions">
                                <ul>
                                  <li className="task-time">
                                    <div className="d-flex flex-column gap-2 mb-2">
                                      <span className="badge badge-tag bg-light text-dark">
                                        <i className="ti ti-calendar-due me-1"></i>
                                        Due: {formatTaskDate(task.due_date)}
                                      </span>
                                    </div>
                                  </li>
                                  <li className="task-owner">
                                    <div className="task-user d-flex align-items-center">
                                      {task.responsible_persons?.full_name ? (
                                        <img
                                          src={avatar}
                                          alt="img"
                                          data-bs-toggle="tooltip"
                                          data-bs-placement="top"
                                          title={
                                            task.responsible_persons.full_name
                                          }
                                          style={{
                                            width: 32,
                                            height: 32,
                                            borderRadius: "50%",
                                            objectFit: "cover",
                                            cursor: "pointer",
                                          }}
                                          onError={handleImageError}
                                        />
                                      ) : (
                                        <div
                                          className="d-flex align-items-center justify-content-center bg-light rounded-circle"
                                          data-bs-toggle="tooltip"
                                          data-bs-placement="top"
                                          title="No assigned user"
                                          style={{
                                            width: 32,
                                            height: 32,
                                            cursor: "pointer",
                                            opacity: 0.6,
                                          }}
                                        >
                                          <i
                                            className="ti ti-user-off text-muted"
                                            style={{ fontSize: "20px" }}
                                          ></i>
                                        </div>
                                      )}
                                    </div>
                                    <div className="dropdown table-action">
                                      <Link
                                        to="#"
                                        className="action-icon "
                                        data-bs-toggle="dropdown"
                                        aria-expanded="false"
                                      >
                                        <i className="fa fa-ellipsis-v" />
                                      </Link>
                                      <div className="dropdown-menu dropdown-menu-right">
                                        <Link
                                          className="dropdown-item"
                                          data-bs-toggle="offcanvas"
                                          to="#"
                                          data-bs-target="#offcanvas_edit"
                                          onClick={() => handleEditTask(task)}
                                        >
                                          <i className="ti ti-edit text-blue"></i>{" "}
                                          Edit
                                        </Link>
                                        <Link
                                          className="dropdown-item"
                                          to="#"
                                          data-bs-toggle="modal"
                                          data-bs-target="#delete_activity"
                                          onClick={() => handleDeleteTask(task)}
                                        >
                                          <i className="ti ti-trash text-danger" />{" "}
                                          Delete
                                        </Link>
                                      </div>
                                    </div>
                                  </li>
                                </ul>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                );
              })}

            {/* Load More Button */}
            {(displayCount < validTasks.length || nextUrl) && (
              <div className="text-center my-4">
                <button
                  className="btn btn-outline-primary"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? "Loading..." : "Load More"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* /Tasks Tab Content */}

      {/* Modal and Offcanvas remain outside of tab structure */}
      <>
        {/* Add New Task */}
        <div
          className="offcanvas offcanvas-end offcanvas-large"
          tabIndex={-1}
          id="offcanvas_add"
          data-bs-backdrop="static"
          data-bs-keyboard="false"
        >
          <div className="offcanvas-header border-bottom">
            <h4>Add New Task</h4>
            <button
              type="button"
              className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
              data-bs-dismiss="offcanvas"
              aria-label="Close"
            >
              <i className="ti ti-x" />
            </button>
          </div>
          <div className="offcanvas-body">
            <form onSubmit={handleSubmit}>
              <div>
                <div className="row">
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="col-form-label">
                        Title <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.title ?? ""}
                        onChange={(e) =>
                          handleInputChange("title", e.target.value)
                        }
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="col-form-label">Assigned Team</label>
                      <div className="select-wrapper">
                        <Select
                          options={teamOptions}
                          value={
                            teamOptions.find(
                              (option) =>
                                option.value === formData.assigned_team
                            ) || null
                          }
                          onChange={(selectedOption: any) => {
                            handleInputChange(
                              "assigned_team",
                              selectedOption?.value || null
                            );
                          }}
                          placeholder="Select a team"
                          className="select"
                          classNamePrefix="react-select"
                          isClearable={true}
                          isSearchable={true}
                          menuPortalTarget={document.body}
                          styles={{
                            control: (provided) => ({
                              ...provided,
                              minHeight: "40px",
                              borderColor: "#ddd",
                              boxShadow: "none",
                              "&:hover": {
                                borderColor: "#007bff",
                              },
                            }),
                            option: (provided, state) => ({
                              ...provided,
                              backgroundColor: state.isSelected
                                ? "#007bff"
                                : state.isFocused
                                  ? "#f8f9fa"
                                  : "white",
                              color: state.isSelected ? "white" : "#333",
                              padding: "8px 12px",
                              cursor: "pointer",
                            }),
                            singleValue: (provided) => ({
                              ...provided,
                              color: "#333",
                            }),
                            placeholder: (provided) => ({
                              ...provided,
                              color: "#999",
                            }),
                            menuPortal: (provided) => ({
                              ...provided,
                              zIndex: 9999,
                            }),
                          }}
                        />
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="col-form-label">
                        Responsible Person
                      </label>
                      <div className="select-wrapper">
                        <Select
                          options={teamMemberOptions}
                          value={
                            teamMemberOptions.find(
                              (option) =>
                                option.value === formData.responsible_person
                            ) || null
                          }
                          onChange={(selectedOption: any) => {
                            handleInputChange(
                              "responsible_person",
                              selectedOption?.value || null
                            );
                          }}
                          placeholder={
                            formData.assigned_team
                              ? "Select a team member"
                              : "Please select a team first"
                          }
                          className="select"
                          classNamePrefix="react-select"
                          isClearable={true}
                          isSearchable={true}
                          isDisabled={!formData.assigned_team}
                          menuPortalTarget={document.body}
                          styles={{
                            control: (provided) => ({
                              ...provided,
                              minHeight: "40px",
                              borderColor: "#ddd",
                              boxShadow: "none",
                              "&:hover": {
                                borderColor: "#007bff",
                              },
                            }),
                            option: (provided, state) => ({
                              ...provided,
                              backgroundColor: state.isSelected
                                ? "#007bff"
                                : state.isFocused
                                  ? "#f8f9fa"
                                  : "white",
                              color: state.isSelected ? "white" : "#333",
                              padding: "8px 12px",
                              cursor: "pointer",
                            }),
                            singleValue: (provided) => ({
                              ...provided,
                              color: "#333",
                            }),
                            placeholder: (provided) => ({
                              ...provided,
                              color: "#999",
                            }),
                            menuPortal: (provided) => ({
                              ...provided,
                              zIndex: 9999,
                            }),
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="col-form-label">
                      Start Date Time <span className="text-danger">*</span>
                    </label>
                    <div className="mb-3">
                      <div className="icon-form">
                        <span className="form-icon">
                          <i className="ti ti-calendar-check" />
                        </span>
                        <DatePicker
                          className="form-control datetimepicker deals-details"
                          selected={formData.start_date}
                          onChange={handleStartDateChange}
                          dateFormat="dd-MM-yyyy h:mm aa"
                          timeFormat="HH:mm"
                          timeIntervals={15}
                          showTimeSelect
                          showTimeSelectOnly={false}
                          timeCaption="Time"
                          timeClassName={() => "time-picker-dropdown"}
                          placeholderText="Choose date and time"
                          required
                          popperPlacement="bottom-start"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="col-form-label">
                      Due Date Time <span className="text-danger">*</span>
                    </label>
                    <div className="mb-3">
                      <div className="icon-form">
                        <span className="form-icon">
                          <i className="ti ti-calendar-check" />
                        </span>
                        <DatePicker
                          className="form-control datetimepicker deals-details"
                          selected={formData.due_date}
                          onChange={handleDueDateChange}
                          dateFormat="dd-MM-yyyy h:mm aa"
                          timeFormat="HH:mm"
                          timeIntervals={15}
                          showTimeSelect
                          showTimeSelectOnly={false}
                          timeCaption="Time"
                          timeClassName={() => "time-picker-dropdown"}
                          placeholderText="Choose date and time"
                          required
                          popperPlacement="bottom-start"
                        />
                      </div>
                      {dateValidationError && (
                        <div className="text-danger small mt-1">
                          {dateValidationError}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="col-form-label">
                        Category <span className="text-danger">*</span>
                      </label>
                      <div className="select-wrapper">
                        <Select
                          options={categoryOptions}
                          className="select"
                          placeholder="Select category"
                          value={formData.category ? categoryOptions.find(
                            (option) => option.value === formData.category
                          ) : null}
                          onChange={(selectedOption: any) => {
                            handleInputChange(
                              "category",
                              selectedOption?.value || ""
                            );
                          }}
                          classNamePrefix="react-select"
                          isClearable={true}
                          isSearchable={true}
                          menuPortalTarget={document.body}
                          styles={{
                            control: (provided) => ({
                              ...provided,
                              minHeight: "40px",
                              borderColor: "#ddd",
                              boxShadow: "none",
                              "&:hover": {
                                borderColor: "#007bff",
                              },
                            }),
                            option: (provided, state) => ({
                              ...provided,
                              backgroundColor: state.isSelected
                                ? "#007bff"
                                : state.isFocused
                                  ? "#f8f9fa"
                                  : "white",
                              color: state.isSelected ? "white" : "#333",
                              padding: "8px 12px",
                              cursor: "pointer",
                            }),
                            singleValue: (provided) => ({
                              ...provided,
                              color: "#333",
                            }),
                            placeholder: (provided) => ({
                              ...provided,
                              color: "#999",
                            }),
                            menuPortal: (provided) => ({
                              ...provided,
                              zIndex: 9999,
                            }),
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  {formData.category === "Meeting" && (
                    <>
                      <div className="col-md-12">
                        <div className="mb-3">
                          <label className="col-form-label">Attendees (Emails)</label>
                          <CreatableSelect
                            isMulti
                            components={{
                              DropdownIndicator: null,
                              IndicatorSeparator: null,
                              Menu: () => null,
                            }}
                            placeholder="Type email and press enter..."
                            inputValue={attendeeInput}
                            onInputChange={(val) => setAttendeeInput(val)}
                            onKeyDown={(e: any) => {
                              if (!attendeeInput) return;
                              if (e.key === "Enter" || e.key === "Tab") {
                                e.preventDefault();
                                if (!formData.attendees.includes(attendeeInput)) {
                                  handleInputChange("attendees", [
                                    ...formData.attendees,
                                    attendeeInput,
                                  ]);
                                }
                                setAttendeeInput("");
                              }
                            }}
                            value={formData.attendees.map((email) => ({
                              label: email,
                              value: email,
                            }))}
                            onChange={(newValue: any) => {
                              const emails = (newValue || []).map(
                                (opt: any) => opt.value
                              );
                              handleInputChange("attendees", emails);
                            }}
                            classNamePrefix="react-select"
                            styles={{
                              control: (base) => ({
                                ...base,
                                borderRadius: "8px",
                                borderColor: "#ddd",
                                minHeight: "40px",
                                boxShadow: "none",
                                "&:hover": {
                                  borderColor: "#007bff",
                                },
                              }),
                              multiValue: (base) => ({
                                ...base,
                                backgroundColor: "#824EEB",
                                borderRadius: "4px",
                              }),
                              multiValueLabel: (base) => ({
                                ...base,
                                color: "#fff",
                              }),
                              multiValueRemove: (base) => ({
                                ...base,
                                color: "#fff",
                                ":hover": {
                                  backgroundColor: "#6b3dc4",
                                  color: "white",
                                },
                              }),
                              menuPortal: (provided) => ({
                                ...provided,
                                zIndex: 9999,
                              }),
                            }}
                            menuPortalTarget={document.body}
                          />
                        </div>
                      </div>
                      <div className="col-md-12">
                        <div className="mb-3 d-flex align-items-center gap-2">
                          <input
                            type="checkbox"
                            className="form-check-input mt-0"
                            id="create_meeting_add"
                            checked={formData.create_meeting}
                            onChange={(e) =>
                              handleInputChange("create_meeting", e.target.checked)
                            }
                          />
                          <label
                            className="form-label fw-semibold mb-0"
                            htmlFor="create_meeting_add"
                          >
                            Create meeting in integrated calendar for this task
                          </label>
                        </div>
                      </div>
                    </>
                  )}
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="col-form-label">
                        Priority <span className="text-danger">*</span>
                      </label>
                      <div className="select-wrapper">
                        <Select
                          options={priorityOptions}
                          className="select2"
                          placeholder="Choose Priority"
                          value={formData.priority ? priorityOptions.find(
                            (option) => option.value === formData.priority
                          ) : null}
                          onChange={(selectedOption: any) => {
                            handleInputChange(
                              "priority",
                              selectedOption?.value || ""
                            );
                          }}
                          classNamePrefix="react-select"
                          isClearable={true}
                          isSearchable={true}
                          menuPortalTarget={document.body}
                          styles={{
                            control: (provided) => ({
                              ...provided,
                              minHeight: "40px",
                              borderColor: "#ddd",
                              boxShadow: "none",
                              "&:hover": {
                                borderColor: "#007bff",
                              },
                            }),
                            option: (provided, state) => ({
                              ...provided,
                              backgroundColor: state.isSelected
                                ? "#007bff"
                                : state.isFocused
                                  ? "#f8f9fa"
                                  : "white",
                              color: state.isSelected ? "white" : "#333",
                              padding: "8px 12px",
                              cursor: "pointer",
                            }),
                            singleValue: (provided) => ({
                              ...provided,
                              color: "#333",
                            }),
                            placeholder: (provided) => ({
                              ...provided,
                              color: "#999",
                            }),
                            menuPortal: (provided) => ({
                              ...provided,
                              zIndex: 9999,
                            }),
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="col-form-label">
                        Status <span className="text-danger">*</span>
                      </label>
                      <div className="select-wrapper">
                        <Select
                          options={statusOptions}
                          className="select"
                          placeholder="Select status"
                          value={formData.status ? statusOptions.find(
                            (option) => option.value === formData.status
                          ) : null}
                          onChange={(selectedOption: any) => {
                            handleInputChange(
                              "status",
                              selectedOption?.value || ""
                            );
                          }}
                          classNamePrefix="react-select"
                          isClearable={true}
                          isSearchable={true}
                          menuPortalTarget={document.body}
                          styles={{
                            control: (provided) => ({
                              ...provided,
                              minHeight: "40px",
                              borderColor: "#ddd",
                              boxShadow: "none",
                              "&:hover": {
                                borderColor: "#007bff",
                              },
                            }),
                            option: (provided, state) => ({
                              ...provided,
                              backgroundColor: state.isSelected
                                ? "#007bff"
                                : state.isFocused
                                  ? "#f8f9fa"
                                  : "white",
                              color: state.isSelected ? "white" : "#333",
                              padding: "8px 12px",
                              cursor: "pointer",
                            }),
                            singleValue: (provided) => ({
                              ...provided,
                              color: "#333",
                            }),
                            placeholder: (provided) => ({
                              ...provided,
                              color: "#999",
                            }),
                            menuPortal: (provided) => ({
                              ...provided,
                              zIndex: 9999,
                            }),
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="col-form-label">
                        Description <span className="text-danger">*</span>
                      </label>
                      <textarea
                        className="form-control"
                        rows={4}
                        value={formData.description ?? ""}
                        onChange={(e) =>
                          handleInputChange("description", e.target.value)
                        }
                        placeholder="Enter description"
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="d-flex align-items-center justify-content-end">
                <button
                  type="button"
                  data-bs-dismiss="offcanvas"
                  className="btn btn-light me-2"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSubmitting || !isFormValid()}
                >
                  {isSubmitting ? "Creating..." : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
        {/* /Add New Task */}
        {/* Edit Task */}
        <div
          className="offcanvas offcanvas-end offcanvas-large"
          tabIndex={-1}
          id="offcanvas_edit"
          data-bs-backdrop="static"
          data-bs-keyboard="false"
        >
          <div className="offcanvas-header border-bottom">
            <h4>Edit Task</h4>
            <button
              type="button"
              className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
              data-bs-dismiss="offcanvas"
              aria-label="Close"
            >
              <i className="ti ti-x" />
            </button>
          </div>
          <div className="offcanvas-body">
            <form onSubmit={handleEditSubmit}>
              <div>
                <div className="row">
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="col-form-label">
                        Title <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={editFormData.title ?? ""}
                        onChange={(e) =>
                          handleEditInputChange("title", e.target.value)
                        }
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="col-form-label">Assigned Team</label>
                      <div className="select-wrapper">
                        <Select
                          options={teamOptions}
                          value={
                            teamOptions.find(
                              (option) =>
                                option.value === editFormData.assigned_team
                            ) || null
                          }
                          onChange={(selectedOption: any) => {
                            handleEditInputChange(
                              "assigned_team",
                              selectedOption?.value || null
                            );
                          }}
                          placeholder="Select a team"
                          className="select"
                          classNamePrefix="react-select"
                          isClearable={true}
                          isSearchable={true}
                          menuPortalTarget={document.body}
                          styles={{
                            control: (provided) => ({
                              ...provided,
                              minHeight: "40px",
                              borderColor: "#ddd",
                              boxShadow: "none",
                              "&:hover": {
                                borderColor: "#007bff",
                              },
                            }),
                            option: (provided, state) => ({
                              ...provided,
                              backgroundColor: state.isSelected
                                ? "#007bff"
                                : state.isFocused
                                  ? "#f8f9fa"
                                  : "white",
                              color: state.isSelected ? "white" : "#333",
                              padding: "8px 12px",
                              cursor: "pointer",
                            }),
                            singleValue: (provided) => ({
                              ...provided,
                              color: "#333",
                            }),
                            placeholder: (provided) => ({
                              ...provided,
                              color: "#999",
                            }),
                            menuPortal: (provided) => ({
                              ...provided,
                              zIndex: 9999,
                            }),
                          }}
                        />
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="col-form-label">
                        Responsible Person
                      </label>
                      <div className="select-wrapper">
                        <Select
                          options={teamMemberOptions}
                          value={
                            teamMemberOptions.find(
                              (option) =>
                                option.value === editFormData.responsible_person
                            ) || null
                          }
                          onChange={(selectedOption: any) => {
                            handleEditInputChange(
                              "responsible_person",
                              selectedOption?.value || null
                            );
                          }}
                          placeholder={
                            editFormData.assigned_team
                              ? "Select a team member"
                              : "Please select a team first"
                          }
                          className="select"
                          classNamePrefix="react-select"
                          isClearable={true}
                          isSearchable={true}
                          isDisabled={!editFormData.assigned_team}
                          menuPortalTarget={document.body}
                          styles={{
                            control: (provided) => ({
                              ...provided,
                              minHeight: "40px",
                              borderColor: "#ddd",
                              boxShadow: "none",
                              "&:hover": {
                                borderColor: "#007bff",
                              },
                            }),
                            option: (provided, state) => ({
                              ...provided,
                              backgroundColor: state.isSelected
                                ? "#007bff"
                                : state.isFocused
                                  ? "#f8f9fa"
                                  : "white",
                              color: state.isSelected ? "white" : "#333",
                              padding: "8px 12px",
                              cursor: "pointer",
                            }),
                            singleValue: (provided) => ({
                              ...provided,
                              color: "#333",
                            }),
                            placeholder: (provided) => ({
                              ...provided,
                              color: "#999",
                            }),
                            menuPortal: (provided) => ({
                              ...provided,
                              zIndex: 9999,
                            }),
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="col-form-label">
                      Start Date Time <span className="text-danger">*</span>
                    </label>
                    <div className="mb-3">
                      <div className="icon-form">
                        <span className="form-icon">
                          <i className="ti ti-calendar-check" />
                        </span>
                        <DatePicker
                          className="form-control datetimepicker deals-details"
                          selected={editFormData.start_date}
                          onChange={handleEditStartDateChange}
                          dateFormat="dd-MM-yyyy h:mm aa"
                          timeFormat="HH:mm"
                          timeIntervals={15}
                          showTimeSelect
                          showTimeSelectOnly={false}
                          timeCaption="Time"
                          timeClassName={() => "time-picker-dropdown"}
                          placeholderText="Choose date and time"
                          required
                          popperPlacement="bottom-start"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="col-form-label">
                      Due Date Time <span className="text-danger">*</span>
                    </label>
                    <div className="mb-3">
                      <div className="icon-form">
                        <span className="form-icon">
                          <i className="ti ti-calendar-check" />
                        </span>
                        <DatePicker
                          className="form-control datetimepicker deals-details"
                          selected={editFormData.due_date}
                          onChange={handleEditDueDateChange}
                          dateFormat="dd-MM-yyyy h:mm aa"
                          timeFormat="HH:mm"
                          timeIntervals={15}
                          showTimeSelect
                          showTimeSelectOnly={false}
                          timeCaption="Time"
                          timeClassName={() => "time-picker-dropdown"}
                          placeholderText="Choose date and time"
                          required
                          popperPlacement="bottom-start"
                        />
                      </div>
                      {editDateValidationError && (
                        <div className="text-danger small mt-1">
                          {editDateValidationError}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="col-form-label">
                        Category <span className="text-danger">*</span>
                      </label>
                      <div className="select-wrapper">
                        <Select
                          options={categoryOptions}
                          className="select"
                          placeholder="Select category"
                          value={categoryOptions.find(
                            (option) => option.value === editFormData.category
                          )}
                          onChange={(selectedOption: any) => {
                            handleEditInputChange(
                              "category",
                              selectedOption?.value || ""
                            );
                          }}
                          classNamePrefix="react-select"
                          isClearable={true}
                          isSearchable={true}
                          menuPortalTarget={document.body}
                          styles={{
                            control: (provided) => ({
                              ...provided,
                              minHeight: "40px",
                              borderColor: "#ddd",
                              boxShadow: "none",
                              "&:hover": {
                                borderColor: "#007bff",
                              },
                            }),
                            option: (provided, state) => ({
                              ...provided,
                              backgroundColor: state.isSelected
                                ? "#007bff"
                                : state.isFocused
                                  ? "#f8f9fa"
                                  : "white",
                              color: state.isSelected ? "white" : "#333",
                              padding: "8px 12px",
                              cursor: "pointer",
                            }),
                            singleValue: (provided) => ({
                              ...provided,
                              color: "#333",
                            }),
                            placeholder: (provided) => ({
                              ...provided,
                              color: "#999",
                            }),
                            menuPortal: (provided) => ({
                              ...provided,
                              zIndex: 9999,
                            }),
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Hide the Edit the Attendees and Meeting for Task */}


                  {/* {editFormData.category === "Meeting" && (
                    <>
                      <div className="col-md-12">
                        <div className="mb-3">
                          <label className="col-form-label">Attendees (Emails)</label>
                          <CreatableSelect
                            isMulti
                            components={{
                              DropdownIndicator: null,
                              IndicatorSeparator: null,
                              Menu: () => null,
                            }}
                            placeholder="Type email and press enter..."
                            inputValue={editAttendeeInput}
                            onInputChange={(val) => setEditAttendeeInput(val)}
                            onKeyDown={(e: any) => {
                              if (!editAttendeeInput) return;
                              if (e.key === "Enter" || e.key === "Tab") {
                                e.preventDefault();
                                if (
                                  !editFormData.attendees.includes(editAttendeeInput)
                                ) {
                                  handleEditInputChange("attendees", [
                                    ...editFormData.attendees,
                                    editAttendeeInput,
                                  ]);
                                }
                                setEditAttendeeInput("");
                              }
                            }}
                            value={editFormData.attendees.map((email) => ({
                              label: email,
                              value: email,
                            }))}
                            onChange={(newValue: any) => {
                              const emails = (newValue || []).map(
                                (opt: any) => opt.value
                              );
                              handleEditInputChange("attendees", emails);
                            }}
                            classNamePrefix="react-select"
                            styles={{
                              control: (base) => ({
                                ...base,
                                borderRadius: "8px",
                                borderColor: "#ddd",
                                minHeight: "40px",
                                boxShadow: "none",
                                "&:hover": {
                                  borderColor: "#007bff",
                                },
                              }),
                              multiValue: (base) => ({
                                ...base,
                                backgroundColor: "#824EEB",
                                borderRadius: "4px",
                              }),
                              multiValueLabel: (base) => ({
                                ...base,
                                color: "#fff",
                              }),
                              multiValueRemove: (base) => ({
                                ...base,
                                color: "#fff",
                                ":hover": {
                                  backgroundColor: "#6b3dc4",
                                  color: "white",
                                },
                              }),
                              menuPortal: (provided) => ({
                                ...provided,
                                zIndex: 9999,
                              }),
                            }}
                            menuPortalTarget={document.body}
                          />
                        </div>
                      </div>
                      <div className="col-md-12">
                        <div className="mb-3 d-flex align-items-center gap-2">
                          <input
                            type="checkbox"
                            className="form-check-input mt-0"
                            id="create_meeting_edit"
                            checked={editFormData.create_meeting}
                            onChange={(e) =>
                              handleEditInputChange(
                                "create_meeting",
                                e.target.checked
                              )
                            }
                          />
                          <label
                            className="form-label fw-semibold mb-0"
                            htmlFor="create_meeting_edit"
                          >
                            Create Meeting in CRM Calendar
                          </label>
                        </div>
                      </div>
                    </>
                  )} */}


                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="col-form-label">
                        Priority <span className="text-danger">*</span>
                      </label>
                      <div className="select-wrapper">
                        <Select
                          options={priorityOptions}
                          className="select2"
                          placeholder="Choose Priority"
                          value={priorityOptions.find(
                            (option) => option.value === editFormData.priority
                          )}
                          onChange={(selectedOption: any) => {
                            handleEditInputChange(
                              "priority",
                              selectedOption?.value || ""
                            );
                          }}
                          classNamePrefix="react-select"
                          isClearable={true}
                          isSearchable={true}
                          menuPortalTarget={document.body}
                          styles={{
                            control: (provided) => ({
                              ...provided,
                              minHeight: "40px",
                              borderColor: "#ddd",
                              boxShadow: "none",
                              "&:hover": {
                                borderColor: "#007bff",
                              },
                            }),
                            option: (provided, state) => ({
                              ...provided,
                              backgroundColor: state.isSelected
                                ? "#007bff"
                                : state.isFocused
                                  ? "#f8f9fa"
                                  : "white",
                              color: state.isSelected ? "white" : "#333",
                              padding: "8px 12px",
                              cursor: "pointer",
                            }),
                            singleValue: (provided) => ({
                              ...provided,
                              color: "#333",
                            }),
                            placeholder: (provided) => ({
                              ...provided,
                              color: "#999",
                            }),
                            menuPortal: (provided) => ({
                              ...provided,
                              zIndex: 9999,
                            }),
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="col-form-label">
                        Status <span className="text-danger">*</span>
                      </label>
                      <div className="select-wrapper">
                        <Select
                          options={statusOptions}
                          className="select"
                          placeholder="Select status"
                          value={statusOptions.find(
                            (option) => option.value === editFormData.status
                          )}
                          onChange={(selectedOption: any) => {
                            handleEditInputChange(
                              "status",
                              selectedOption?.value || ""
                            );
                          }}
                          classNamePrefix="react-select"
                          isClearable={true}
                          isSearchable={true}
                          menuPortalTarget={document.body}
                          styles={{
                            control: (provided) => ({
                              ...provided,
                              minHeight: "40px",
                              borderColor: "#ddd",
                              boxShadow: "none",
                              "&:hover": {
                                borderColor: "#007bff",
                              },
                            }),
                            option: (provided, state) => ({
                              ...provided,
                              backgroundColor: state.isSelected
                                ? "#007bff"
                                : state.isFocused
                                  ? "#f8f9fa"
                                  : "white",
                              color: state.isSelected ? "white" : "#333",
                              padding: "8px 12px",
                              cursor: "pointer",
                            }),
                            singleValue: (provided) => ({
                              ...provided,
                              color: "#333",
                            }),
                            placeholder: (provided) => ({
                              ...provided,
                              color: "#999",
                            }),
                            menuPortal: (provided) => ({
                              ...provided,
                              zIndex: 9999,
                            }),
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="col-form-label">
                        Description <span className="text-danger">*</span>
                      </label>
                      <textarea
                        className="form-control"
                        rows={4}
                        value={editFormData.description ?? ""}
                        onChange={(e) =>
                          handleEditInputChange("description", e.target.value)
                        }
                        placeholder="Enter description"
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="d-flex align-items-center justify-content-end">
                <button
                  type="button"
                  data-bs-dismiss="offcanvas"
                  className="btn btn-light me-2"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isUpdating || !isEditFormValid()}
                >
                  {isUpdating ? "Updating..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
        {/* /Edit Task */}

        {/* Delete Task */}
        <div className="modal fade" id="delete_activity" role="dialog">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-body">
                <div className="text-center">
                  <div className="avatar avatar-xl bg-danger-light rounded-circle mb-3">
                    <i className="ti ti-trash-x fs-36 text-danger" />
                  </div>
                  <h4 className="mb-2">Remove Task?</h4>
                  <p className="mb-0">
                    Are you sure you want to remove <br />
                    {taskToDelete
                      ? `"${taskToDelete.title}"`
                      : "the selected task"}
                    ?
                  </p>
                  <div className="d-flex align-items-center justify-content-center mt-4">
                    <Link
                      to="#"
                      className="btn btn-light me-2"
                      data-bs-dismiss="modal"
                    >
                      Cancel
                    </Link>
                    <button
                      type="button"
                      className="btn btn-danger"
                      data-bs-dismiss="modal"
                      onClick={confirmDeleteTask}
                      disabled={isDeleting}
                    >
                      {isDeleting ? "Deleting..." : "Yes, Delete it"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* /Delete Task */}
        {/* Add New View */}
        <div className="modal custom-modal fade" id="save_view" role="dialog">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add New View</h5>
                <button
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  <i className="ti ti-x" />
                </button>
              </div>
              <div className="modal-body">
                <form>
                  <div className="mb-3">
                    <label className="col-form-label">View Name</label>
                    <input type="text" className="form-control" />
                  </div>
                  <div className="modal-btn text-end">
                    <Link
                      to="#"
                      className="btn btn-light"
                      data-bs-dismiss="modal"
                    >
                      Cancel
                    </Link>
                    <button type="submit" className="btn btn-danger">
                      Save
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
        {/* /Add New View */}

        {/* TaskDetailModal */}
        <TaskDetailModal
          isVisible={isTaskModalVisible}
          taskDetails={taskDetails}
          isLoading={isLoadingTaskDetails}
          onClose={handleTaskModalClose}
          onTaskUpdate={handleTaskUpdate}
          tenantId={tenant_id}
        />
      </>
    </>
  );
};

export default Task;
